import React, { useState, useEffect } from 'react';
import { Save, RefreshCw, AlertCircle, Check, X, Play, Pause, CreditCard, X as XIcon } from 'lucide-react';
import toast from 'react-hot-toast';
import { getPaymentConfig, updatePaymentConfig } from '../../modules/payment/services/api';
import type { PaymentConfig } from '../../modules/payment/types';
import { roomApi, Room } from '../../utils/roomApi';

const AdminRoomManagement: React.FC = () => {
  const [config, setConfig] = useState<Partial<PaymentConfig>>({});
  const [rooms, setRooms] = useState<Room[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<'config' | 'rooms'>('config');
  const [showRenewModal, setShowRenewModal] = useState(false);
  const [renewRoomId, setRenewRoomId] = useState<number | null>(null);
  const [selectedDuration, setSelectedDuration] = useState<'monthly' | 'quarterly' | 'yearly'>('monthly');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadConfig();
    loadRooms();
  }, []);

  const loadConfig = async () => {
    try {
      const result = await getPaymentConfig();
      if (result.success) {
        setConfig(result.data);
      }
    } catch (error) {
      console.error('加载配置失败:', error);
      toast.error('加载配置失败');
    }
  };

  const loadRooms = async (search?: string) => {
    try {
      const result = await roomApi.getRooms(true, search);
      if (result.success) {
        setRooms(result.data);
      }
    } catch (error) {
      console.error('加载房间列表失败:', error);
      toast.error('加载房间列表失败');
    }
  };
  
  const handleSearch = () => {
    loadRooms(searchTerm);
  };

  const handleSaveConfig = async () => {
    setSaving(true);
    try {
      const result = await updatePaymentConfig(config);
      if (result.success) {
        toast.success('配置保存成功');
        await loadConfig();
      } else {
        toast.error(result.message || '保存失败');
      }
    } catch (error) {
      console.error('保存配置失败:', error);
      toast.error('保存配置失败');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (path: string, value: string) => {
    const keys = path.split('.');
    setConfig(prev => {
      const newConfig = { ...prev };
      let current = newConfig as any;
      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) current[keys[i]] = {};
        current = current[keys[i]];
      }
      let finalValue: any = value;
      if (path.includes('Price')) {
        finalValue = parseFloat(value) || 0;
      } else if (path.includes('enabled')) {
        finalValue = value === 'true';
      }
      current[keys[keys.length - 1]] = finalValue;
      return newConfig;
    });
  };

  const handleRoomStatusChange = async (roomId: number, status: string) => {
    try {
      const result = await roomApi.updateRoomStatus(roomId, status);
      if (result.success) {
        toast.success(status === 'active' ? '房间已启用' : '房间已暂停');
        await loadRooms();
      } else {
        toast.error(result.message || '操作失败');
      }
    } catch (error) {
      console.error('更新房间状态失败:', error);
      toast.error('更新房间状态失败');
    }
  };

  const handleRenew = (roomId: number) => {
    setRenewRoomId(roomId);
    setSelectedDuration('monthly');
    setShowRenewModal(true);
  };

  const handleConfirmRenew = async () => {
    if (!renewRoomId) return;
    try {
      const result = await roomApi.renewRoom(renewRoomId, selectedDuration);
      if (result.success) {
        toast.success('续费成功');
        setShowRenewModal(false);
        await loadRooms();
      } else {
        toast.error(result.message || '续费失败');
      }
    } catch (error) {
      console.error('续费失败:', error);
      toast.error('续费失败');
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '永久';
    const date = new Date(dateString);
    return date.toLocaleDateString('zh-CN');
  };

  const getRoomExpiresAt = (room: Room) => {
    return room.expires_at || room.room_creator_expires_at;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-800">会员房间管理</h1>
        <div className="flex gap-2">
          {activeTab === 'config' && (
            <button
              onClick={handleSaveConfig}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50"
            >
              <Save className="w-4 h-4" />
              保存配置
            </button>
          )}
          <button
            onClick={() => {
              loadConfig();
              loadRooms();
            }}
            className="flex items-center gap-2 px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            刷新
          </button>
        </div>
      </div>

      <div className="flex gap-2 border-b">
        <button
          onClick={() => setActiveTab('config')}
          className={`px-4 py-2 font-medium rounded-t-lg transition-colors ${
            activeTab === 'config'
              ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          套餐配置
        </button>
        <button
          onClick={() => setActiveTab('rooms')}
          className={`px-4 py-2 font-medium rounded-t-lg transition-colors ${
            activeTab === 'rooms'
              ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          房间列表
        </button>
      </div>

      {activeTab === 'config' && (
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">房间套餐配置</h3>
            
            <div className="grid md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  月费 (USDT)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={config.roomPackage?.monthlyPrice || ''}
                  onChange={(e) => handleChange('roomPackage.monthlyPrice', e.target.value)}
                  placeholder="月费"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  季费 (USDT)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={config.roomPackage?.quarterlyPrice || ''}
                  onChange={(e) => handleChange('roomPackage.quarterlyPrice', e.target.value)}
                  placeholder="季费"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  年费 (USDT)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={config.roomPackage?.yearlyPrice || ''}
                  onChange={(e) => handleChange('roomPackage.yearlyPrice', e.target.value)}
                  placeholder="年费"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  启用
                </label>
                <select
                  value={config.roomPackage?.enabled === true ? 'true' : 'false'}
                  onChange={(e) => handleChange('roomPackage.enabled', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
                >
                  <option value="true">是</option>
                  <option value="false">否</option>
                </select>
              </div>
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                套餐标题
              </label>
              <input
                type="text"
                value={config.roomPackage?.title || ''}
                onChange={(e) => handleChange('roomPackage.title', e.target.value)}
                placeholder="套餐标题"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
              />
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                套餐描述
              </label>
              <textarea
                value={config.roomPackage?.description || ''}
                onChange={(e) => handleChange('roomPackage.description', e.target.value)}
                placeholder="套餐描述"
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
              />
            </div>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start">
              <AlertCircle className="w-5 h-5 text-yellow-600 mr-2 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-yellow-800">说明</p>
                <p className="text-yellow-700 mt-1">
                  1. 套餐费用设置后，房主购买套餐时将根据选择的周期（月/季/年）计算价格<br />
                  2. 套餐到期后，系统将自动暂停房主所有房间的使用权<br />
                  3. 系统每分钟检查一次过期房间
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'rooms' && (
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b">
            <h3 className="text-lg font-semibold text-gray-800">房间列表</h3>
            <div className="mt-4 flex items-center">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="搜索房间ID/房主/房间名称..."
                className="w-64 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
              />
              <button
                onClick={handleSearch}
                className="ml-3 px-4 py-2 bg-pink-500 text-white rounded-lg hover:bg-pink-600 transition-colors"
              >
                搜索
              </button>
              {searchTerm && (
                <button
                  onClick={() => {
                    setSearchTerm('');
                    loadRooms();
                  }}
                  className="ml-2 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  重置
                </button>
              )}
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    房间ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    房间名称
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    房主
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    价格 (USDT)
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    会员数
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    状态
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    创建时间
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    到期时间
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    操作
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {rooms.map((room) => (
                  <tr key={room.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-blue-600">{room.id}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{room.name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">{room.owner_name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">
                        {room.price_type === 'monthly' ? '月' : room.price_type === 'quarterly' ? '季' : '年'} {room.price} USDT
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">{room.member_count || 0}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        room.is_active === 1
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {room.is_active === 1 ? '正常' : '暂停'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">{formatDate(room.created_at)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className={`text-sm ${
                        getRoomExpiresAt(room) && new Date(getRoomExpiresAt(room)!) < new Date()
                          ? 'text-red-600 font-medium'
                          : 'text-gray-500'
                      }`}>
                        {formatDate(getRoomExpiresAt(room) || '')}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleRoomStatusChange(room.id, room.is_active === 1 ? 'paused' : 'active')}
                          className={`inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                            room.is_active === 1
                              ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                              : 'bg-green-100 text-green-700 hover:bg-green-200'
                          }`}
                        >
                          {room.is_active === 1 ? (
                            <>
                              <Pause className="w-4 h-4" />
                              暂停
                            </>
                          ) : (
                            <>
                              <Play className="w-4 h-4" />
                              启用
                            </>
                          )}
                        </button>
                        {room.is_active !== 1 && (
                          <button
                            onClick={() => handleRenew(room.id)}
                            className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors"
                          >
                            <CreditCard className="w-4 h-4" />
                            续费
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {rooms.length === 0 && (
              <div className="text-center py-12">
                <AlertCircle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">暂无房间</p>
              </div>
            )}
          </div>
        </div>
      )}

      {showRenewModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold text-gray-800">房间续费</h3>
              <button
                onClick={() => setShowRenewModal(false)}
                className="p-1 hover:bg-gray-100 rounded-full transition-colors"
              >
                <XIcon className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="p-4">
              <p className="text-gray-600 mb-4">选择续费时长：</p>
              <div className="space-y-3">
                <label className="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                  <input
                    type="radio"
                    name="duration"
                    value="monthly"
                    checked={selectedDuration === 'monthly'}
                    onChange={(e) => setSelectedDuration(e.target.value as 'monthly' | 'quarterly' | 'yearly')}
                    className="mr-3"
                  />
                  <span className="font-medium text-gray-800">月付</span>
                  <span className="ml-auto text-blue-600 font-semibold">
                    {config.roomPackage?.monthlyPrice || 0} USDT
                  </span>
                </label>
                <label className="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                  <input
                    type="radio"
                    name="duration"
                    value="quarterly"
                    checked={selectedDuration === 'quarterly'}
                    onChange={(e) => setSelectedDuration(e.target.value as 'monthly' | 'quarterly' | 'yearly')}
                    className="mr-3"
                  />
                  <span className="font-medium text-gray-800">季付</span>
                  <span className="ml-auto text-blue-600 font-semibold">
                    {config.roomPackage?.quarterlyPrice || 0} USDT
                  </span>
                </label>
                <label className="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                  <input
                    type="radio"
                    name="duration"
                    value="yearly"
                    checked={selectedDuration === 'yearly'}
                    onChange={(e) => setSelectedDuration(e.target.value as 'monthly' | 'quarterly' | 'yearly')}
                    className="mr-3"
                  />
                  <span className="font-medium text-gray-800">年付</span>
                  <span className="ml-auto text-blue-600 font-semibold">
                    {config.roomPackage?.yearlyPrice || 0} USDT
                  </span>
                </label>
              </div>
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowRenewModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  取消
                </button>
                <button
                  onClick={handleConfirmRenew}
                  className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                >
                  确认续费
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminRoomManagement;
